/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {EntityElementModel} from '../../../../shared/entity-tree/entity-element/models/entity-element.model';
import {DecisionCodeModel} from '../../../../api/ms-settings/business/schema/v1/contracts/pool/decision-code.model';
import {CountryModel} from '../../../../api/ms-settings/business/schema/v1/contracts/pool/country.model';
import {PartnerModel} from '../../../../api/ms-settings/business/schema/v1/contracts/pool/partner.model';
import {ShipToPartyModel} from '../../../../api/ms-settings/business/schema/v1/contracts/pool/ship-to-party.model';
import {SupplyingPackingPlantModel} from '../../../../api/ms-settings/business/schema/v1/contracts/pool/supplying-packing-plant.model';
import {TransportModeModel} from '../../../../api/ms-settings/business/schema/v1/contracts/pool/transport-mode.model';
import {TransportServiceModel} from '../../../../api/ms-settings/business/schema/v1/contracts/pool/transport-service.model';
import {LeadTimeModel} from '../../../../api/ms-settings/business/schema/v1/contracts/pool/lead-time.model';
import {PeriodTypeEnum} from '../../../../api/generic/contracts/period-type.enum';
import {WarningModel} from '../../../../api/ms-settings/business/schema/v1/contracts/pool/warning.model';
import {ReasonModel} from '../../../../api/ms-settings/business/schema/v1/contracts/pool/reason.model';
import {ClaimCodeModel} from '../../../../api/ms-settings/business/schema/v1/contracts/pool/claim-code.model';
import {ErrorCodeModel} from '../../../../api/ms-settings/business/schema/v1/contracts/pool/error-code.model';
import {BusinessCategoryModel} from '../../../../api/ms-settings/business/schema/v1/contracts/pool/business-category.model';
import {TechnicianGroupModel} from '../../../../api/ms-settings/business/schema/v1/contracts/pool/technician-group.model';
import {ContextMenuActionEnum} from '../../../../shared/entity-tree/entity-element/models/context-menu-action.enum';

/**
 * Countries entity functions
 */
const countriesDisplayCode = (country: CountryModel): string => `${country.alpha2Code} ${country.alpha3Code} ${country.numericCode}`;

const countriesChildrenCode = (country: CountryModel): string => country.alpha3Code;

const countriesMapOriginalValue = (newElement: EntityElementModel): CountryModel => ({
    code: newElement.code,
    name: newElement.name,
    active: newElement.active,
    alpha2Code: newElement.currentValue.alpha2Code,
    alpha3Code: newElement.currentValue.alpha3Code,
    numericCode: newElement.currentValue.numericCode
});

/**
 * Partner entity functions
 */
const partnersChildrenCode = (partner: EntityElementModel): string => partner.displayParentCode;

const partnersMapOriginalValue = (newElement: EntityElementModel): PartnerModel => ({
    code: newElement.code,
    name: newElement.name,
    active: newElement.active,
    sequencer: newElement.currentValue.sequencer
});

/**
 * Ship to parties entity functions
 */
const shipToPartiesMapOriginalValue = (newElement: EntityElementModel): ShipToPartyModel => ({
    code: newElement.code,
    name: newElement.name,
    active: newElement.active
});

/**
 * Supplying packing plants entity functions
 */
const supplyingPackingPlantsMapOriginalValue = (newElement: EntityElementModel): SupplyingPackingPlantModel => ({
    code: newElement.code,
    name: newElement.name,
    active: newElement.active,
    integrationChannelCode: newElement.currentValue.integrationChannelCode,
    number: newElement.currentValue.number
});

/**
 * Transport modes entity functions
 */
const transportModesMapOriginalValue = (newElement: EntityElementModel): TransportModeModel => ({
    code: newElement.code,
    name: newElement.name,
    active: newElement.active,
    documentTypeCode: newElement.currentValue.documentTypeCode
});

/**
 * Transport services entity functions
 */

const transportServiceHasIcon = (transportService: TransportServiceModel): boolean => transportService.requiresJustification;

const transportServicesMapOriginalValue = (newElement: EntityElementModel<TransportServiceModel>): TransportServiceModel => ({
    code: newElement.code,
    name: newElement.name,
    active: newElement.active,
    requiresJustification: newElement.currentValue.requiresJustification
});

/**
 * Lead times entity functions
 */

// TODO: delete once it came now from the database configuration
const PeriodTypeDisplayString = new Map<string, string>([
    [PeriodTypeEnum.DAYS, 'days'],
    [PeriodTypeEnum.WEEKS, 'weeks'],
    [PeriodTypeEnum.MONTHS, 'months'],
    [PeriodTypeEnum.YEARS, 'years']
]);

// TODO: delete once it came now from the database configuration
const PeriodTypeMagnitude = new Map<string, number>([
    [PeriodTypeEnum.DAYS, 0],
    [PeriodTypeEnum.WEEKS, 1],
    [PeriodTypeEnum.MONTHS, 2],
    [PeriodTypeEnum.YEARS, 3]
]);

const leadTimesDisplayName = (leadTime: LeadTimeModel): string => `${leadTime.value}`;

const leadTimesDisplayCode = (leadTime: LeadTimeModel): string => `${PeriodTypeDisplayString.get(leadTime.periodTypeCode)}`;

const leadTimeSortingValue = (leadTime: LeadTimeModel): string =>
        `${PeriodTypeMagnitude.get(leadTime.periodTypeCode)} ${leadTime.value.toString()
                .padStart(4, '0')}`;

const leadTimesMapOriginalValue = (newElement: EntityElementModel): LeadTimeModel => ({
    code: newElement.code,
    active: newElement.active,
    periodTypeCode: newElement.currentValue.periodTypeCode,
    value: newElement.currentValue.value
});

const leadTimeDisplayModalName = (leadTime: LeadTimeModel): string =>
        `${leadTime.value} ${PeriodTypeDisplayString.get(leadTime.periodTypeCode)}`;

/**
 * Warnings entity functions
 */
const warningsDisplayName = (warning: WarningModel): string => `${warning.description}`;

const warningsSortingValue = (warning: WarningModel): string => `${warning.description}`;

const warningsMapOriginalValue = (newElement: EntityElementModel<WarningModel>): WarningModel => ({
    active: newElement.active,
    code: newElement.code,
    description: newElement.currentValue.description
});

const warningsDisplayModalName = (warning: WarningModel): string => `"${warning.description}"`;

/**
 * Reasons entity functions
 */
const reasonsMapOriginalValue = (newElement: EntityElementModel): ReasonModel => ({
    code: newElement.code,
    name: newElement.name,
    active: newElement.active
});

/**
 * Claim codes entity functions
 */
const claimCodesHasIcon = (claimCode: ClaimCodeModel): boolean => claimCode.requiresAttachments;

const claimCodesMapOriginalValue = (newElement: EntityElementModel): ClaimCodeModel => ({
    code: newElement.code,
    name: newElement.name,
    active: newElement.active,
    requiresAttachments: newElement.currentValue.requiresAttachments
});

/**
 * Decision codes entity functions
 */

const decisionCodesHasIcon = (decisionCode: DecisionCodeModel): boolean => decisionCode.hasTimeEffort;

const decisionCodesMapOriginalValue = (newElement: EntityElementModel<DecisionCodeModel>): DecisionCodeModel => ({
    code: newElement.code,
    name: newElement.name,
    active: newElement.active,
    hasTimeEffort: newElement.currentValue.hasTimeEffort,
    excludedDecisionCodes: newElement.currentValue.excludedDecisionCodes
});

const updateExistingCodesInExcludedDecision = (changedElement: EntityElementModel<DecisionCodeModel>,
                                               pool: EntityElementModel<DecisionCodeModel>[]): EntityElementModel<DecisionCodeModel>[] => {
    pool.map((item) => {
        if (item.currentValue.excludedDecisionCodes != null) {
            item.currentValue.excludedDecisionCodes.splice(
                    item.currentValue.excludedDecisionCodes.findIndex((entry) =>
                            entry === changedElement.originalValue.code),
                    1,
                    changedElement.currentValue.code
            );
        }
        return item;
    });
    return pool;
};

const updateCodesInExcludedDecisions = (changedElement: EntityElementModel<DecisionCodeModel>,
                                        pool: EntityElementModel<DecisionCodeModel>[]): EntityElementModel<DecisionCodeModel>[] => {
    pool.map((item) => {
        if (item.currentValue.excludedDecisionCodes == null) {
            item.currentValue.excludedDecisionCodes = [];
        }
        const itemExcludedDecisionCodes = item.currentValue.excludedDecisionCodes;
        const elementExcludedDecisionCodes = changedElement.currentValue.excludedDecisionCodes;

        // changedElement has item.code in its excludedDecisionCodes and item.excludedDecisionCodes does not have changedElement.code
        if (elementExcludedDecisionCodes.includes(item.currentValue.code)
                && !itemExcludedDecisionCodes.includes(changedElement.currentValue.code)) {
            itemExcludedDecisionCodes.push(changedElement.currentValue.code);
        }
        // changedElement does not have item.code in its excludedDecisionCodes and item.excludedDecisionCodes has changedElement.code
        if (!elementExcludedDecisionCodes.includes(item.currentValue.code)
                && itemExcludedDecisionCodes.includes(changedElement.currentValue.code)) {
            itemExcludedDecisionCodes.splice(
                    itemExcludedDecisionCodes.findIndex((value) => value === changedElement.currentValue.code), 1);
        }

        return {
            ...item,
            currentValue: {
                ...item.currentValue,
                excludedDecisionCodes: itemExcludedDecisionCodes
            }
        };
    });
    return pool;
};

const removeCodeInExcludedDecisions = (deletedElement: EntityElementModel<DecisionCodeModel>,
                                       pool: EntityElementModel<DecisionCodeModel>[]): EntityElementModel<DecisionCodeModel>[] => {
    if (deletedElement != null && pool != null) {
        pool.forEach((item) => {
            if (item.currentValue.excludedDecisionCodes != null
                    && item.currentValue.excludedDecisionCodes.length > 0
                    && item.currentValue.excludedDecisionCodes.includes(deletedElement.currentValue.code)) {
                item.currentValue.excludedDecisionCodes.splice(
                        item.currentValue.excludedDecisionCodes.findIndex((value) => value === deletedElement.currentValue.code), 1);
            }
        });
    }

    return pool;
};

const decisionCodeUpdatePoolItems = (changedElement: EntityElementModel<DecisionCodeModel>,
                                     pool: EntityElementModel<DecisionCodeModel>[]): EntityElementModel<DecisionCodeModel>[] => {
    if (changedElement.currentValue.code !== changedElement.originalValue.code) {
        // eslint-disable-next-line no-use-before-define
        configurationFunctions.decisionCodes.updateExistingCodesInExcludedDecision(changedElement, pool);
    }

    if (changedElement.currentValue.excludedDecisionCodes != null) {
        // eslint-disable-next-line no-use-before-define
        configurationFunctions.decisionCodes.updateCodesInExcludedDecisions(changedElement, pool);
    }

    return pool;
};

/**
 * Error codes entity functions
 */

const errorCodesMapOriginalValue = (newElement: EntityElementModel): ErrorCodeModel => ({
    code: newElement.code,
    name: newElement.name,
    active: newElement.active
});

/**
 * BI category entity functions
 */

const biCategoryMapOriginalValue = (newElement: EntityElementModel): BusinessCategoryModel => ({
    code: newElement.code,
    name: newElement.name,
    active: newElement.active,
    range: newElement.currentValue.range
});
const biCategorySortingAndDisplayCode = (biCategory: BusinessCategoryModel): string => `${biCategory.range}`;

/**
 * Technicians groups entity functions
 */

export const techniciansGroupWithModelCodesInConnections = (connections: EntityElementModel<TechnicianGroupModel>[]): boolean => {
    let hasModelCodes = false;
    if (connections?.find((item) => item.currentValue.modelCodes?.length > 0)) {
        hasModelCodes = true;
    }
    return hasModelCodes;
};

export const techniciansGroupInConnections = (element: EntityElementModel<TechnicianGroupModel>,
                                              connections: EntityElementModel<TechnicianGroupModel>[]): boolean => {
    let inConnections = false;
    if (connections?.find((item) => item?.code === element?.code)) {
        inConnections = true;
    }
    return inConnections;
};

const technicianGroupMapOriginalValue = (newElement: EntityElementModel): TechnicianGroupModel => ({
    code: newElement.code,
    name: newElement.name,
    active: newElement.active,
    modelCodes: newElement.currentValue.modelCodes
});

const technicianGroupsDisplayCode = (newElement: TechnicianGroupModel): string => {
    let code = null;
    if (newElement.modelCodes != null && newElement.modelCodes.length > 0) {
        code = newElement.modelCodes.join('/');
    }
    return code;
};

const technicianGroupsAfterAddToConnections = (element: EntityElementModel<TechnicianGroupModel>,
                                               pool: EntityElementModel<TechnicianGroupModel>[]):
        EntityElementModel<TechnicianGroupModel>[] => {
    if (element != null && pool != null) {
        if (element.currentValue.modelCodes == null || element.currentValue.modelCodes.length === 0) {
            pool.forEach((item) => item.contextMenuActions = [ContextMenuActionEnum.DELETE, ContextMenuActionEnum.EDIT]);
        } else {
            pool.forEach((item) => {
                if (item.currentValue.modelCodes == null || item.currentValue.modelCodes.length === 0) {
                    item.contextMenuActions = [ContextMenuActionEnum.DELETE, ContextMenuActionEnum.EDIT];
                }
            });
        }
    }
    return pool;
};

const technicianGroupsAfterRemoveFromConnections = (pool: EntityElementModel<TechnicianGroupModel>[],
                                                    connections: EntityElementModel<TechnicianGroupModel>[]):
        EntityElementModel<TechnicianGroupModel>[] => {
    if (pool != null) {
        if (connections?.length === 0) {
            pool.forEach((item) => {
                if (!item.contextMenuActions?.includes(ContextMenuActionEnum.CONNECT)) {
                    item.contextMenuActions = [...item.contextMenuActions, ContextMenuActionEnum.CONNECT];
                } else if (connections.find((entry) => entry.currentValue.code === item.currentValue.code) != null) {
                    item.contextMenuActions = [ContextMenuActionEnum.DELETE, ContextMenuActionEnum.EDIT];
                }
            });
        } else {
            if (connections.some((item) => item.currentValue.modelCodes == null || item.currentValue.modelCodes.length === 0)) {
                pool.forEach((item) => item.contextMenuActions = [ContextMenuActionEnum.DELETE, ContextMenuActionEnum.EDIT]);
            } else {
                pool.forEach((item) => {
                    if (item.currentValue.modelCodes == null || item.currentValue.modelCodes.length === 0) {
                        item.contextMenuActions = [ContextMenuActionEnum.DELETE, ContextMenuActionEnum.EDIT];
                    } else if (connections.find((entry) => entry.currentValue.code === item.currentValue.code) == null
                            && !item.contextMenuActions?.includes(ContextMenuActionEnum.CONNECT)) {
                        item.contextMenuActions = [...item.contextMenuActions, ContextMenuActionEnum.CONNECT];
                    }
                });
            }
        }
    }
    return pool;
};

const techniciansGroupsBreadcrumbLabel = (technicianGroup: EntityElementModel<TechnicianGroupModel>): string => technicianGroup.name;

const techniciansGroupPoolNewItemContextMenuActions = (element: EntityElementModel<TechnicianGroupModel>,
                                                       pool: EntityElementModel<TechnicianGroupModel>[],
                                                       connections: EntityElementModel<TechnicianGroupModel>[]):
        ContextMenuActionEnum[] => {

    const contextMenuActions = [ContextMenuActionEnum.DELETE, ContextMenuActionEnum.EDIT];
    if (element != null && pool != null && connections != null) {

        if (connections.length) {
            if (techniciansGroupWithModelCodesInConnections(connections) && element.currentValue?.modelCodes?.length > 0) {
                contextMenuActions.push(ContextMenuActionEnum.CONNECT);
            }
        } else {
            contextMenuActions.push(ContextMenuActionEnum.CONNECT);
        }
    }
    return contextMenuActions;
};

const techniciansGroupsUpdatePoolContextMenuActions = (pool: EntityElementModel<TechnicianGroupModel>[],
                                                       connections: EntityElementModel<TechnicianGroupModel>[]):
        EntityElementModel<TechnicianGroupModel>[] => {

    if (pool != null && connections != null) {
        const contextMenuActions = [ContextMenuActionEnum.DELETE, ContextMenuActionEnum.EDIT];

        if (connections.length) {
            if (techniciansGroupWithModelCodesInConnections(connections)) {
                pool.forEach((item) => {
                    if (item.currentValue?.modelCodes?.length > 0 && !techniciansGroupInConnections(item, connections)) {
                        item.contextMenuActions = [ContextMenuActionEnum.CONNECT, ...contextMenuActions];
                    } else {
                        item.contextMenuActions = [...contextMenuActions];
                    }
                });
            } else {
                pool.forEach((item) => {
                    item.contextMenuActions = [...contextMenuActions];
                });
            }
        } else {
            pool.forEach((item) => item.contextMenuActions = [ContextMenuActionEnum.CONNECT, ...contextMenuActions]);
        }
    }

    return pool;
};

/**
 * Variable to export functions
 */
const configurationFunctions = {
    countries: {
        countriesDisplayCode,
        countriesChildrenCode,
        countriesMapOriginalValue
    },
    partners: {
        partnersChildrenCodeFunction: partnersChildrenCode,
        partnersMapOriginalValue
    },
    shipToParties: {
        shipToPartiesMapOriginalValue
    },
    supplyingPackingPlants: {
        supplyingPackingPlantsMapOriginalValue
    },
    transportModes: {
        transportModesMapOriginalValue
    },
    transportServices: {
        transportServicesMapOriginalValue,
        transportServiceHasIcon
    },
    leadTimes: {
        leadTimesDisplayCode,
        leadTimesDisplayName,
        leadTimeSortingValue,
        leadTimesMapOriginalValue,
        leadTimeDisplayModalName
    },
    warnings: {
        warningsDisplayName,
        warningsSortingValue,
        warningsMapOriginalValue,
        warningsDisplayModalName
    },
    reasons: {
        reasonsMapOriginalValue
    },
    claimCodes: {
        claimCodesHasIcon,
        claimCodesMapOriginalValue
    },
    decisionCodes: {
        decisionCodeUpdatePoolItems,
        decisionCodesHasIcon,
        decisionCodesMapOriginalValue,
        updateCodesInExcludedDecisions,
        removeCodeInExcludedDecisions,
        updateExistingCodesInExcludedDecision
    },
    errorCodes: {
        errorCodesMapOriginalValue
    },
    businessCategories: {
        biCategoryMapOriginalValue,
        biCategorySortingAndDisplayCode
    },
    technicianGroups: {
        technicianGroupMapOriginalValue,
        technicianGroupsDisplayCode,
        technicianGroupsAfterAddToConnections,
        technicianGroupsAfterRemoveFromConnections,
        techniciansGroupsBreadcrumbLabel,
        techniciansGroupPoolNewItemContextMenuActions,
        techniciansGroupsUpdatePoolContextMenuActions
    }
};

export default configurationFunctions;


